import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ClipboardCheck, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { todayIstIso } from "@/utils/date";

const fmt = (iso) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y.slice(2)}`; };
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function TeamWeekCard({ t, week, onSaved }) {
  const [counts, setCounts] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setCounts(Object.fromEntries(t.items.map((i) => [i.target_id, i.done])));
    setNote(t.submitted?.note || "");
  }, [t]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/targets/week-submit", { team: t.team, week_start: week, note, items: t.items.map((i) => ({ target_id: i.target_id, count: Number(counts[i.target_id]) || 0 })) });
      toast.success(`${t.team}: week submitted`); onSaved(); window.dispatchEvent(new Event("lim:nudges-refresh"));
    } catch (x) { toast.error(formatApiError(x?.response?.data?.detail) || "Submit failed"); } finally { setBusy(false); }
  };
  const slug = t.team.replace(/\W+/g, "-");
  return (
    <div className={`rounded-lg border p-3 ${t.submitted ? "border-emerald-500/30" : "border-[#E11D2E]/40"}`} data-testid={`week-submit-team-${slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="font-semibold">{t.team}</div>
        {t.submitted
          ? <span className="text-xs inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300" data-testid={`week-submit-status-${slug}`}><CheckCircle2 size={13} /> Submitted by {t.submitted.by_name} · {new Date(t.submitted.at).toLocaleString("en-IN")}</span>
          : <span className="text-xs inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-semibold" data-testid={`week-submit-status-${slug}`}><AlertTriangle size={13} /> Pending</span>}
      </div>
      <div className="space-y-1.5">
        {t.items.map((i) => (
          <div key={i.target_id} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 text-sm">
            <div className="min-w-0 truncate">{i.name}{i.assignee_name && <span className="text-white/50"> · {i.assignee_name}</span>}</div>
            <div className="text-xs text-white/55">target {i.week_target}</div>
            <input type="number" min="0" value={counts[i.target_id] ?? 0} disabled={!t.can_submit}
              onChange={(e) => setCounts({ ...counts, [i.target_id]: e.target.value })} data-testid={`week-submit-count-${i.target_id}`}
              className="h-8 w-20 rounded-md border border-white/15 bg-transparent px-2 text-sm disabled:opacity-60" aria-label={`Delivered for ${i.name}`} />
          </div>
        ))}
      </div>
      {t.can_submit && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — what blocked, what's next"
            data-testid={`week-submit-note-${slug}`} className="flex-1 min-w-[200px] h-9 rounded-md border border-white/15 bg-transparent px-3 text-sm" />
          <Button onClick={submit} disabled={busy} data-testid={`week-submit-btn-${slug}`} className="h-9 bg-[#E11D2E] hover:bg-[#F03041] text-white">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{t.submitted ? "Update week" : "Submit week"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function WeekSubmitPanel() {
  const { search } = useLocation();
  const ref = useRef(null);
  const weeks = useMemo(() => {
    const d = new Date(`${todayIstIso()}T00:00:00`); d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return Array.from({ length: 6 }, (_, i) => { const x = new Date(d); x.setDate(x.getDate() - 7 * i); return isoOf(x); });
  }, []);
  const [week, setWeek] = useState(weeks[1]);
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/targets/week-status", { params: { week_start: week } }); setData(d); } catch { setData({ teams: [] }); }
  }, [week]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (search.includes("submit=1") && data) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [search, data]);
  if (!data || data.teams.length === 0) return null;
  const pending = data.teams.filter((t) => !t.submitted).length;
  return (
    <section ref={ref} className="glass rounded-xl p-4" data-testid="week-submit-panel">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <ClipboardCheck size={16} className="text-[#E11D2E]" />
        <h2 className="text-base font-semibold">Weekly target log</h2>
        <select value={week} onChange={(e) => setWeek(e.target.value)} data-testid="week-submit-week" className="h-8 rounded-md border border-white/15 bg-surface px-2 text-sm">
          {weeks.map((w, i) => {
            const e = new Date(`${w}T00:00:00`); e.setDate(e.getDate() + 6);
            return <option key={w} value={w}>{fmt(w)} – {fmt(isoOf(e))}{i === 0 ? " (this week)" : i === 1 ? " (last week)" : ""}</option>;
          })}
        </select>
        <span className={`text-xs ${pending ? "text-red-600 dark:text-red-400 font-semibold" : "text-emerald-700 dark:text-emerald-300"}`} data-testid="week-submit-pending-count">
          {pending ? `${pending} department${pending > 1 ? "s" : ""} pending` : "All submitted"}
        </span>
      </div>
      <p className="text-xs text-white/55 mb-3">Managers confirm what each department delivered against its weekly target (enter 0 if nothing). Reminders keep coming until the week is submitted.</p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {data.teams.map((t) => <TeamWeekCard key={t.team} t={t} week={data.week_start} onSaved={load} />)}
      </div>
    </section>
  );
}
